/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  OnDestroy,
  Input,
  OnInit,
} from '@angular/core';
import { SidebarHttpService } from './../../services/sidebar-http.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { FacSnackbarOptions, SnackbarService } from '@foglight/angular-common';
import { EMPTY, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { Bookmark } from '../../models/navigation-menu-item';
@Component({
  selector: 'fc-add-bookmark-dialog',
  templateUrl: './add-bookmark-dialog.component.html',
  styleUrls: ['./add-bookmark-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [SnackbarService],
})
export class AddBookmarkDialogComponent implements OnInit, OnDestroy {
  emailChecked = false;
  emailLink = 'light-theme-link';
  formGroup: UntypedFormGroup;
  private destroy$ = new Subject<void>();
  @Input() inputData: Bookmark | undefined;

  constructor(
    private dialogRef: MatDialogRef<AddBookmarkDialogComponent>,
    private sidebarHttpService: SidebarHttpService,
    private snackbarService: SnackbarService
  ) {
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(this.inputData?.name || '', [Validators.required, Validators.maxLength(45)]),
    });
  }

  ngOnInit(): void {
    if(localStorage.getItem('theme') === 'dark-theme') {
      this.emailLink = 'dark-theme-link';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAddClick() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.inputData = {
      ...this.inputData,
      name: this.formGroup.get('name')?.value || '',
    } as Bookmark;

    this.sidebarHttpService
      .getBookmarks()
      .pipe(takeUntil(this.destroy$))
      .subscribe((bookmarks) => {
        const existedData = bookmarks.find(
          (bookmark) => bookmark.link === this.inputData?.link
        );
        if (!existedData) {
          this.sidebarHttpService
            .createBookmark(this.inputData as Bookmark)
            .pipe(takeUntil(this.destroy$), catchError(() => { this.dismissDialog('error', false); return EMPTY; }))
            .subscribe(
              (bookmark: Bookmark) => {
                this.dismissDialog('success', this.emailChecked, bookmark);
              }
            );
        } else {
          this.snackBarError();
        }
      });
  }

  sendEmailLink(event?: { checked?: boolean } | boolean) {
    if (typeof event === 'boolean') {
      this.emailChecked = event;
      return;
    }

    if (event && typeof event.checked === 'boolean') {
      this.emailChecked = event.checked;
      return;
    }

    this.emailChecked = !this.emailChecked;
  }

  snackBarError() {
    const options: FacSnackbarOptions = {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      duration: 3000,
      data: {
        type: 'error',
        text: 'Duplicate link',
      },
    };

    this.snackbarService.show(options);
  }

  dismissDialog(message: string, email?: boolean, bookmark?: Bookmark) {
    this.dialogRef.close({
      message,
      email,
      bookmark,
    });
  }
}
